import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, Target, CheckCircle2 } from 'lucide-react';
const SERVICES_LIST = ['Fitness Coaching', 'Nutrition & Meal Prep', 'Rest & Recovery', 'Virtual Assistant', 'Executive Support', 'Small Business Compliance', 'Cleaning Services', 'Property Management', 'Relocation & Settling-In', 'Travel Planning', 'Event Planning', 'Personal Concierge', 'Digital Learning'];
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function Community() {
  const [newsletterState, setNewsletterState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [regState, setRegState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [regErrors, setRegErrors] = useState<Record<string, string>>({});
  const handleNewsletter = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    if (formData.get('bot-field')) return;
    setNewsletterState('loading');
    setTimeout(() => setNewsletterState('success'), 1500);
  };
  const handleRegistration = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    if (formData.get('bot-field')) return;
    const newErrors: Record<string, string> = {};
    if (!formData.get('firstName')) newErrors.firstName = 'Required';
    if (!formData.get('lastName')) newErrors.lastName = 'Required';
    const email = formData.get('email') as string;
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) newErrors.email = 'Valid email required';
    if (!formData.get('phone')) newErrors.phone = 'Required';
    if (Object.keys(newErrors).length > 0) {
      setRegErrors(newErrors);
      return;
    }
    setRegErrors({});
    setRegState('loading');
    setTimeout(() => setRegState('success'), 1500);
  };
  return <main className="flex flex-col min-h-screen bg-warmwhite pt-20" data-id="element-167" data-mp="2p8z1dupft8ye">
      {/* HERO SECTION */}
      <section className="bg-gradient-to-br from-plum via-plum-light to-fuchsia py-24 text-center px-4" data-id="element-168" data-mp="2u8277hd8rj7f">
        <motion.div initial="hidden" animate="visible" variants={fadeUpVariants} className="max-w-3xl mx-auto" data-id="element-169" data-mp="2z5btwp98xgrk">
          <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-6" data-id="element-170" data-mp="38w6s8rzro6td">
            Join thousands of women choosing clarity.
          </h1>
          <p className="text-lg md:text-xl text-white/90 font-body leading-relaxed" data-id="element-171" data-mp="3cvp28sq1uxhf">
            The Silvermark community is where ambition meets support. You don't
            have to do this alone.
          </p>
        </motion.div>
      </section>

      {/* NEWSLETTER SIGNUP */}
      <section className="py-16 -mt-12 relative z-10 px-4" data-id="element-172" data-mp="1njvl7e20gkrl">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-charcoal/5" data-id="element-173" data-mp="1shexi8f5engw">
          <div className="text-center mb-8" data-id="element-174" data-mp="1kye3qeu5g86q">
            <h2 className="font-display text-3xl font-bold text-plum mb-2" data-id="element-175" data-mp="1kzgummrh2amn">
              Stay in the loop
            </h2>
            <p className="text-charcoal/70" data-id="element-176" data-mp="1b8cfnn1sqprt">
              Weekly insights on wellness, productivity, and living with
              intention.
            </p>
          </div>

          {newsletterState === 'success' ? <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center py-6" data-id="element-177" data-mp="23xjmrafbgnoo">
              <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" data-id="element-178" data-mp="1ozzloeljjcns" />
              <p className="text-xl font-medium text-charcoal" data-id="element-179" data-mp="1p23pyxncqdol">
                You're on the list!
              </p>
            </motion.div> : <form onSubmit={handleNewsletter} className="flex flex-col md:flex-row gap-4 max-w-2xl mx-auto" data-id="element-180" data-mp="djj7sppmx431">
              <input type="text" name="bot-field" className="hidden" data-id="element-181" data-mp="275hwxr1z1kqe" />
              <input type="text" name="firstName" placeholder="First Name" required className="flex-1 px-6 py-4 rounded-full bg-warmwhite border border-charcoal/10 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia focus:outline-none transition-colors" data-id="element-182" data-mp="276t0bpri8qgz" />
              <input type="email" name="email" placeholder="Email Address" required className="flex-1 px-6 py-4 rounded-full bg-warmwhite border border-charcoal/10 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia focus:outline-none transition-colors" data-id="element-183" data-mp="2q4vu0huzzbl4" />
              <button type="submit" disabled={newsletterState === 'loading'} className="px-8 py-4 bg-gold text-charcoal font-medium rounded-full hover:bg-gold-light transition-colors flex justify-center items-center disabled:opacity-70" data-id="element-184" data-mp="zqlkdw9bswng">
                {newsletterState === 'loading' ? <div className="w-6 h-6 border-2 border-charcoal/30 border-t-charcoal rounded-full animate-spin" data-id="element-185" data-mp="3rzqiarxq1ct7" /> : 'Join the Movement'}
              </button>
            </form>}
        </div>
      </section>

      {/* COMMUNITY PRINCIPLES */}
      <section className="py-24 bg-warmwhite" data-id="element-186" data-mp="2va957rwesohh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-187" data-mp="2v7d4xwx39npv">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-188" data-mp="391e5mw7lpksv">
            {[{
            icon: Users,
            title: 'Show Up Authentically',
            desc: 'This is a space for real women with real lives. No pretending.'
          }, {
            icon: TrendingUp,
            title: 'Lift As You Rise',
            desc: "We celebrate each other's wins and support through challenges."
          }, {
            icon: Target,
            title: 'Choose Clarity Daily',
            desc: 'Small, intentional choices compound into extraordinary lives.'
          }].map((pillar, i) => {
            const Icon = pillar.icon;
            return <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={{
              hidden: {
                opacity: 0,
                y: 30
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  delay: i * 0.2,
                  duration: 0.6
                }
              }
            }} className="text-center flex flex-col items-center" data-id="element-189" data-mp="3spdwc455ioru" data-mpi={i}>
                  <div className="w-16 h-16 rounded-2xl bg-fuchsia/10 flex items-center justify-center mb-6" data-id="element-190" data-mp="32gy14f0k1icr">
                    <Icon className="w-8 h-8 text-fuchsia" data-id="element-191" data-mp="2sp1z4svcl839" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-charcoal mb-4" data-id="element-192" data-mp="2smxvmc1d2b3i">
                    {pillar.title}
                  </h3>
                  <p className="text-charcoal/70 leading-relaxed" data-id="element-193" data-mp="2jn131zbuos36">
                    {pillar.desc}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* FULL REGISTRATION FORM */}
      <section className="py-24 bg-white border-y border-charcoal/5" data-id="element-194" data-mp="cxmh0uw0ouya">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-195" data-mp="cwuwqg7vejnb">
          <div className="text-center mb-12" data-id="element-196" data-mp="7zkz4dabv1oe">
            <h2 className="font-display text-4xl font-bold text-plum mb-4" data-id="element-197" data-mp="7wgq92lx3hre">
              Become a Silvermark Member
            </h2>
            <p className="text-lg text-charcoal/70 max-w-2xl mx-auto" data-id="element-198" data-mp="3hy7ngr85lyyq">
              Join our community and get access to exclusive events, resources,
              and a network of incredible women.
            </p>
          </div>

          <div className="bg-warmwhite p-8 md:p-12 rounded-3xl shadow-sm border border-charcoal/5" data-id="element-199" data-mp="386jzj0at3020">
            {regState === 'success' ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-center py-12" data-id="element-200" data-mp="2z76qg8qlgdht">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" data-id="element-201" data-mp="2pa22c3d6u7yd" />
                <h3 className="font-display text-3xl font-bold text-charcoal mb-4" data-id="element-202" data-mp="ii1b35xxs7ef">
                  Welcome to the community!
                </h3>
                <p className="text-charcoal/70 text-lg" data-id="element-203" data-mp="ngun0txkrqch">
                  We're thrilled to have you. Check your inbox for your welcome
                  package.
                </p>
              </motion.div> : <form onSubmit={handleRegistration} className="space-y-8" data-id="element-204" data-mp="2hphpycj295j9">
                <input type="text" name="bot-field" className="hidden" data-id="element-205" data-mp="2hqsvags5mlch" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-206" data-mp="2cxzolnd4l2xr">
                  <div data-id="element-207" data-mp="2qpmtgbl4vjs5">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-208" data-mp="2qouy91vg7o2e">
                      First Name
                    </label>
                    <input type="text" name="firstName" className={`w-full px-4 py-3 rounded-xl border ${regErrors.firstName ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-white`} data-id="element-209" data-mp="30oc3ji6sfzxu" />
                  </div>
                  <div data-id="element-210" data-mp="11cjce26olot1">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-211" data-mp="11dcf8s2iru5w">
                      Last Name
                    </label>
                    <input type="text" name="lastName" className={`w-full px-4 py-3 rounded-xl border ${regErrors.lastName ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-white`} data-id="element-212" data-mp="g92irbdhqsfh" />
                  </div>
                  <div data-id="element-213" data-mp="1410u1vcxwnsm">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-214" data-mp="z03ezrn889vc">
                      Email Address
                    </label>
                    <input type="email" name="email" className={`w-full px-4 py-3 rounded-xl border ${regErrors.email ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-white`} data-id="element-215" data-mp="u7k1svsgbgr2" />
                  </div>
                  <div data-id="element-216" data-mp="30lpb0l22p0p2">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-217" data-mp="30m7ikdu6r1ca">
                      Phone Number
                    </label>
                    <input type="tel" name="phone" className={`w-full px-4 py-3 rounded-xl border ${regErrors.phone ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-white`} data-id="element-218" data-mp="35mmud0ytk0s5" />
                  </div>
                  <div data-id="element-219" data-mp="3oil7obn87uf4">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-220" data-mp="3ohtoy1eq9r69">
                      City
                    </label>
                    <input type="text" name="city" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-white" data-id="element-221" data-mp="16atu9hjj4ui" />
                  </div>
                  <div data-id="element-222" data-mp="277mnt2y700n0">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-223" data-mp="276v2qm281laz">
                      How did you hear about us?
                    </label>
                    <select name="source" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-white appearance-none" data-id="element-224" data-mp="226p7kvwqtgo5">
                      <option value="" data-id="element-225" data-mp="2pwjhb4lrmp46">Select an option</option>
                      <option value="instagram" data-id="element-226" data-mp="2lx2bub6c9rz0">Instagram</option>
                      <option value="tiktok" data-id="element-227" data-mp="2lyvihqgycc70">TikTok</option>
                      <option value="linkedin" data-id="element-228" data-mp="2zuzbjgg3aehw">LinkedIn</option>
                      <option value="referral" data-id="element-229" data-mp="2uxh11lmfbr7s">Friend / Referral</option>
                      <option value="google" data-id="element-230" data-mp="2uveb11l9ty32">Google</option>
                      <option value="event" data-id="element-231" data-mp="15j2ffa6pckgi">Event</option>
                      <option value="other" data-id="element-232" data-mp="10jfzgszhoq40">Other</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-233" data-mp="3c7kx2kc38rit">
                  <label className="block text-sm font-medium text-charcoal mb-4" data-id="element-234" data-mp="3c71lrmgy11dr">
                    Areas of Interest (Select all that apply)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-235" data-mp="32fown2mj4pbf">
                    {SERVICES_LIST.map(service => <label key={service} className="flex items-center gap-3 cursor-pointer group" data-id="element-236" data-mp="37e079fknf3up">
                        <div className="relative flex items-center justify-center" data-id="element-237" data-mp="2yg68zz3bq04w">
                          <input type="checkbox" name="interests" value={service} className="peer appearance-none w-5 h-5 border-2 border-charcoal/20 rounded bg-white checked:bg-fuchsia checked:border-fuchsia transition-colors cursor-pointer" data-id="element-238" data-mp="2jinppc9f0f4u" />
                          <CheckCircle2 className="w-3.5 h-3.5 text-white absolute opacity-0 peer-checked:opacity-100 pointer-events-none" data-id="element-239" data-mp="2isgu4kg8ygsp" />
                        </div>
                        <span className="text-sm text-charcoal/80 group-hover:text-charcoal transition-colors" data-id="element-240" data-mp="2ep12y3my58ne">
                          {service}
                        </span>
                      </label>)}
                  </div>
                </div>

                <button type="submit" disabled={regState === 'loading'} className="w-full md:w-auto px-10 py-4 bg-fuchsia text-white rounded-full font-medium hover:bg-fuchsia-light transition-colors flex justify-center items-center disabled:opacity-70" data-id="element-241" data-mp="1zvfm0qrec0kj">
                  {regState === 'loading' ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-242" data-mp="1h3mxp9n9v3j5" /> : 'Join the Community'}
                </button>
              </form>}
          </div>
        </div>
      </section>

      {/* SOCIAL MEDIA STRIP */}
      <section className="py-24 bg-warmwhite" data-id="element-243" data-mp="16xlkyk009reb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-244" data-mp="1bxzuk6h999eg">
          <h2 className="font-display text-3xl font-bold text-plum mb-12" data-id="element-245" data-mp="1bwonnxsijlj4">
            Follow the journey
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12" data-id="element-246" data-mp="jadsj2noectp">
            {['https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?q=80&w=2053&auto=format&fit=crop', 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop', 'https://images.unsplash.com/photo-1542596594-649edbc13630?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1556761175-5973dc0f32d7?q=80&w=1932&auto=format&fit=crop', 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?q=80&w=2070&auto=format&fit=crop'].map((img, i) => <a key={i} href="#" className="relative aspect-square rounded-xl overflow-hidden group block" data-id="element-247" data-mp="77b1u2gsdizz" data-mpi={i}>
                <img src={img} alt="Instagram post" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-248" data-mp="2au54jvitwba" />
                <div className="absolute inset-0 bg-fuchsia/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center" data-id="element-249" data-mp="3km7griwle2g1">
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-250" data-mp="3fm2zvrrrv5pa">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" data-id="element-251" data-mp="17fhk587m6254" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" data-id="element-252" data-mp="17doenw8qlnyp" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" data-id="element-253" data-mp="thkoqf4wc1s1" />
                  </svg>
                </div>
              </a>)}
          </div>
        </div>
      </section>

      {/* PARTNERS ROW */}
      <section className="py-16 bg-white border-t border-charcoal/5" data-id="element-254" data-mp="1l98uujx2zs2f">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-255" data-mp="1q4xns4eliapr">
          <h3 className="text-sm font-medium text-charcoal/50 uppercase tracking-widest mb-8" data-id="element-256" data-mp="1cfnewx96k4rf">
            Proudly supported by
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-50 grayscale" data-id="element-257" data-mp="1gemes3g21bc0">
            {['Lumina', 'Aura Health', 'Verve', 'Equinox', 'Zenith', 'Nova'].map(partner => <div key={partner} className="font-display font-bold text-2xl text-charcoal" data-id="element-258" data-mp="1k870tsc2xomi">
                {partner}
              </div>)}
          </div>
        </div>
      </section>
    </main>;
}